import { FC } from "react";
import { FileText, ReceiptEuro } from "lucide-react";
import { Tooltip } from "@mui/material";
import { ShoppingCart } from "@mui/icons-material";
import { BaseTransactionDetailsDto } from "../../../../api-client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { formatDateUI } from "../../../../helpers/dateFormat";
import { useDownload } from "../../../../hooks/useDownload";
import { transactionTypes } from "../../../Transactions/Kasse/v2/types/kasse.types";

interface TransactionTableProps {
  allTransactions: BaseTransactionDetailsDto[];
}

const thClass =
  "h-12 py-3 text-center text-sm font-semibold uppercase tracking-wide text-slate-600";

/**
 * Drop-in replacement for `PatientAllTransactionTable` using the SEPA card design
 * (SepaBankAccountsCard): a shadcn Card with an icon title, a bordered inner table
 * with centered uppercase headers and zebra rows, and the teal ghost download icon.
 * Functionality and data are identical to the original.
 */
const TransactionTable: FC<TransactionTableProps> = ({ allTransactions }) => {
  const { handleDownloadPdf } = useDownload();

  return (
    <Card className="mt-3 overflow-hidden rounded-lg border border-solid border-black shadow-none">
      <CardHeader className="bg-slate-50/60 py-4">
        <CardTitle className="flex items-center gap-2 text-xl font-semibold text-slate-800">
          <ReceiptEuro className="h-5 w-5 text-slate-500" />
          Alle Transaktionen
        </CardTitle>
        <p className="mt-1 text-left text-sm text-slate-500">
          Bargeld-, EC-, SEPA- und Rechnungszahlungen.
        </p>
      </CardHeader>

      <CardContent className="pt-2">
        <div className="overflow-hidden rounded border border-slate-200">
          <Table>
            <TableHeader>
              <TableRow className="border-b border-solid border-slate-200 bg-slate-100 hover:bg-slate-100">
                <TableHead className={`${thClass} text-left`}>Transaktion Nummer</TableHead>
                <TableHead className={`${thClass} text-left`}>Rezeptnummer</TableHead>
                <TableHead className={`${thClass} text-left`}>RezeptDatum</TableHead>
                <TableHead className={`${thClass} text-left`}>Typ</TableHead>
                <TableHead className={`${thClass} text-right`}>Gesamtpreis (€)</TableHead>
                <TableHead className={thClass}>Erstellungsdatum</TableHead>
                <TableHead className={thClass}>Erstellt von</TableHead>
                <TableHead className={thClass}>Belege</TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {allTransactions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="py-10 text-center text-sm text-slate-400">
                    Keine Transaktionen vorhanden.
                  </TableCell>
                </TableRow>
              ) : (
                allTransactions.map((transaction, index) => {
                  const zebra = index % 2 === 1 ? "bg-slate-100" : "bg-white";
                  // A cancelled/reversed transaction (e.g. from a revoked SEPA mandate) keeps its
                  // Typ but is voided; the cash/EC storno (anti) row is a Stornierung that isn't
                  // flagged isCancelled — treat both as voided so they mute.
                  const isCancelled = transaction.isCancelled ?? false;
                  const isVoided = isCancelled || transaction.type === transactionTypes.CANCEL;
                  const isNegative =
                    transaction.type === transactionTypes.CANCEL ||
                    transaction.type === transactionTypes.AUSGABE ||
                    (transaction.amount < 0 && transaction.type === transactionTypes.REFUND);
                  const showAbs =
                    transaction.amount < 0 ||
                    transaction.type === transactionTypes.CANCEL ||
                    transaction.type === transactionTypes.REFUND ||
                    transaction.type === transactionTypes.AUSGABE;
                  return (
                    <TableRow
                      key={index}
                      className={`text-base ${zebra} hover:bg-slate-100/70 ${
                        isVoided ? "[&_td]:text-slate-400" : ""
                      }`}
                    >
                      <TableCell
                        className={`py-1 text-left font-medium text-slate-800 ${
                          isVoided ? "line-through" : ""
                        }`}
                      >
                        {transaction.transactionNumber}
                      </TableCell>
                      <TableCell className="py-1 text-left text-slate-700">
                        {transaction?.rx?.rxNumber || (
                          <ShoppingCart fontSize="small" className="text-slate-400" />
                        )}
                      </TableCell>
                      <TableCell className="whitespace-nowrap py-1 text-left text-slate-600">
                        {transaction?.rx?.rxDate ? formatDateUI(transaction.rx.rxDate) : "-"}
                      </TableCell>
                      <TableCell
                        className={`py-1 text-left ${
                          isNegative ? "text-rose-600" : "text-slate-700"
                        }`}
                      >
                        {transaction.type}
                      </TableCell>
                      <TableCell
                        className={`whitespace-nowrap py-1 text-right font-semibold ${
                          isNegative ? "text-rose-600" : "text-slate-800"
                        }`}
                      >
                        {showAbs
                          ? `${Math.abs(Number(transaction.amount)).toFixed(2).replace(".", ",")} €`
                          : `${Number(transaction.amount).toFixed(2).replace(".", ",")} €`}
                      </TableCell>
                      <TableCell className="whitespace-nowrap py-1 text-center text-slate-600">
                        {formatDateUI(transaction.createdAt)}
                      </TableCell>
                      <TableCell className="py-1 text-center text-slate-600">
                        {transaction.user.firstName + " " + transaction.user.lastName}
                      </TableCell>
                      <TableCell className="py-1 text-center">
                        {transaction.docDto?.id ? (
                          <Tooltip title="Beleg herunterladen">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 border-0 bg-transparent text-[#367b9a] shadow-none hover:bg-transparent hover:text-[#367b9a] focus-visible:ring-0 focus-visible:ring-offset-0 [&_svg]:size-6"
                              onClick={() => handleDownloadPdf(transaction.docDto!.id)}
                              aria-label="Beleg herunterladen"
                            >
                              <FileText />
                            </Button>
                          </Tooltip>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};

export default TransactionTable;
